// Copyright (c) 2024 by Audere

import {
  AddLabelRequest,
  AddQCFeedbackRequest,
  FormLabelData,
  LabelingRole,
  MessageRole,
  UpdateLabelRequest,
  useAddLabelMutation,
} from "../../features/api/labelApiSlice";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CompletionStatus,
  LabelTaskKey,
  generateFormDefaultValues,
  getQCCorrectedLabels,
  getQCCorrection,
  isMessageTask,
  isStructuredDataTask,
  isSummaryTask,
  isTranslatedMessageTask,
} from "../../utils/taskUtils";
import { ErrorAlert, InfoAlert, SnackbarAlert } from "../Alerts";
import { FormProvider, useForm } from "react-hook-form";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  FlagOption,
  LabelTask,
  Question,
  getFlagOption,
  getQuestions,
} from "../../features/label/labelTypes";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createStyleSheet, globalStyles } from "../../utils/styles";
import {
  updateLastUpdatedTasks,
  updateQCCorrectLabels,
  updateTaskStatuses,
} from "../../features/slices/labelSlice";
import { useAppDispatch, useAppSelector } from "../../features/store";

import DebugPanel from "../DebugPanel";
import { Delete } from "@mui/icons-material";
import MultiSelectQuestionComponent from "../MultiSelectQuestionComponent";
import SelectQuestionComponent from "../SelectQuestionComponent";
import SmallChip from "../SmallChip";
import TextQuestionComponent from "../TextQuestionComponent";
import { formatValueAsString } from "../../utils/stringUtils";
import { getTaskLabels } from "../../utils/labelUtils";
import { hasSubmittedLabels } from "../../features/data/annotatorStatusUtils";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useLabelingSchema } from "../../hooks/useSchema";
import { useLabels } from "../../hooks/useLabels";
import { useParams } from "react-router-dom";
import { useStructuredData } from "../../hooks/useStructuredData";
import { useSummary } from "../../hooks/useSummary";
import { useTranslation } from "react-i18next";
import { usesStagingApi } from "../../utils/config";
import { grey } from "@mui/material/colors";
import { valueMatches } from "../../utils/validateQuestion";
import { logEvent } from "../../features/api/apiSlice";
import { LabelingEvent } from "../../features/api/logging";
import { useLabelsForMonitor } from "../../hooks/useLabelsForMonitor";
import { getConversationHook } from "../../features/data/conversationUtils";

export interface LabelingFormProps {
  task: LabelTask;
  debugEnabled?: boolean;
  formProvider: LabelingRole;
  messageUID?: string;
  messageRole?: MessageRole;
  onSave: (payload: UpdateLabelRequest | AddQCFeedbackRequest) => void;
}

const LabelingForm: React.FC<LabelingFormProps> = ({
  task,
  debugEnabled,
  messageUID,
  messageRole,
  formProvider,
  onSave,
}) => {
  const { conversationUID, labelTypeID } = useParams();
  const { isQC, isLabeler, group, email, role, isMonitor } = useCurrentUser();
  const dispatch = useAppDispatch();
  const useConversation = getConversationHook(role);
  const { conversation } = useConversation(
    conversationUID ?? "",
    labelTypeID ?? ""
  );

  const labeler = useAppSelector((state) => state.label.labeler);
  const qc = useAppSelector((state) => state.label.qc);
  const qcCorrectLabels = useAppSelector(
    (state) => state.label.qcCorrectLabels
  );
  const lastUpdatedTasks = useAppSelector(
    (state) => state.label.lastUpdatedTasks
  );
  const { structuredData, isFetching: isStructuredDataFetching } =
    useStructuredData(conversationUID ?? "");
  const { summary, isFetching: isSummaryFetching } = useSummary(
    conversationUID ?? ""
  );
  const { t } = useTranslation();
  const {
    labels: existingLabels,
    error,
    refetch: refetchLabels,
  } = useLabels(conversationUID ?? "");
  const { labels: allExistingLabels } = useLabelsForMonitor(
    conversationUID ?? ""
  );
  const [open, setOpen] = React.useState(false);
  const [addLabel] = useAddLabelMutation();
  const { labelingQuestions, labelingSchemaVersion } = useLabelingSchema();
  const questions: Question[] = getQuestions(
    task.questionIDs,
    labelingQuestions,
    messageRole
  );

  const isCorrect = getQCCorrection(qcCorrectLabels, task.id, messageUID);
  const [isLoading, setIsLoading] = useState(true);
  const hasCreatedLabelRef = useRef(false);
  const hasScrolledRef = useRef(false);
  const logCommonData = useMemo(
    () => ({
      userEmail: email,
      userRole: role,
      userGroup: group,
      conversationUid: conversationUID ?? "",
      labelType: labelTypeID,
      taskID: task.id as LabelTaskKey,
      messageUid: messageUID,
    }),
    [email, role, group, conversationUID, labelTypeID, task.id, messageUID]
  );

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const methods = useForm<FormLabelData>({ defaultValues: {} });
  const { reset, setValue, getValues, watch } = methods;

  const formData = getValues();
  if (isQC) {
    formData.correct_label = isCorrect;
  }

  const getFormSubmission = () => {
    if (isMonitor) {
      return true;
    }
    if (isQC && formProvider === LabelingRole.conversation_label_qc) {
      return hasSubmittedLabels(qc?.qc_status);
    } else if (
      isLabeler ||
      formProvider === LabelingRole.conversation_labeler
    ) {
      return hasSubmittedLabels(labeler?.labeler_status);
    }
  };
  const formSubmitted = getFormSubmission();

  const saveData = useCallback(
    (value: FormLabelData) => {
      const data = JSON.parse(JSON.stringify(value)) as FormLabelData; // Deep clone the form data
      const labels = isMonitor
        ? allExistingLabels?.labels
        : existingLabels?.labels;
      const currentLabelUID = getTaskLabels(
        task.id,
        labels,
        messageUID
      )?.label_uid;

      const payload = {
        group: group,
        conversation_uid: conversationUID ?? "",
        ...((isMessageTask(task.content) ||
          isTranslatedMessageTask(task.content)) &&
        messageUID
          ? { message_uid: messageUID }
          : {}),
        labeling_task: task.id,
        label_uid: currentLabelUID ?? "",
      };

      if (!formSubmitted && isLabeler) {
        const labelerPayload: UpdateLabelRequest = {
          ...payload,
          labeling_type: labelTypeID ?? "",
          label_data: data,
        };
        onSave(labelerPayload);
      }
      if (isQC) {
        const qcPayload: AddQCFeedbackRequest = {
          ...payload,
          labeler_uid: labeler?.labeler_uid ?? "",
          qc_data: data,
        };
        onSave(qcPayload);
      }
    },
    [
      conversationUID,
      labelTypeID,
      messageUID,
      task,
      group,
      isLabeler,
      formSubmitted,
      labeler?.labeler_uid,
      isQC,
      existingLabels,
      allExistingLabels,
      isMonitor,
      onSave,
    ]
  );

  useEffect(() => {
    const labels = isMonitor
      ? allExistingLabels?.labels
      : existingLabels?.labels;
    const taskLabels = getTaskLabels(task.id, labels, messageUID);

    if (labels && taskLabels && taskLabels.label_uid !== "*") {
      const savedData =
        formProvider === LabelingRole.conversation_labeler
          ? taskLabels.label_data
          : { ...taskLabels.label_data, ...taskLabels.qc_data };

      if (Object.keys(savedData).length > 0) {
        Object.entries(savedData).forEach(([key, value]) => {
          setValue(key, value);
        });
      }
      setIsLoading(false);
    }
  }, [
    task.id,
    existingLabels,
    formProvider,
    messageUID,
    setValue,
    isMonitor,
    allExistingLabels?.labels,
  ]);

  useEffect(() => {
    const labels = isMonitor
      ? allExistingLabels?.labels
      : existingLabels?.labels;
    const taskLabels = getTaskLabels(task.id, labels, messageUID);
    if (labels && !taskLabels && isLabeler) {
      const payload: AddLabelRequest = {
        group: group,
        conversation_uid: conversationUID ?? "",
        ...((isMessageTask(task.content) ||
          isTranslatedMessageTask(task.content)) &&
        messageUID
          ? { message_uid: messageUID }
          : {}),
        labeling_type: labelTypeID ?? "",
        labeling_task: task.id,
        labeling_schema_version: labelingSchemaVersion,
        label_data: getValues(),
        ...(isStructuredDataTask(task.content) && {
          labeled_content: structuredData,
        }),
        ...(isSummaryTask(task.content) && {
          labeled_content: summary,
        }),
      };
      if (
        !hasCreatedLabelRef.current &&
        !isStructuredDataFetching &&
        !isSummaryFetching
      ) {
        hasCreatedLabelRef.current = true;
        void addLabel(payload);
      }
    }
  }, [
    group,
    isLabeler,
    labelTypeID,
    conversationUID,
    existingLabels,
    messageUID,
    task,
    getValues,
    setValue,
    addLabel,
    labelingSchemaVersion,
    isMonitor,
    allExistingLabels?.labels,
    structuredData,
    summary,
  ]);

  useEffect(() => {
    if (!existingLabels && isQC) {
      void refetchLabels();
    }
  }, [existingLabels, isQC, refetchLabels]);

  useEffect(() => {
    if (!isStructuredDataTask(task.content)) return;

    const lastMessageUID = lastUpdatedTasks[task.id as LabelTaskKey];
    if (!lastMessageUID || !Object.keys(structuredData).length) return;
    if (hasScrolledRef.current) return;

    const container = document.getElementById("task-content");
    if (!container) return;

    const timeout = setTimeout(() => {
      const element = container.querySelector<HTMLElement>(
        `[id="${lastMessageUID}"]`
      );
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        hasScrolledRef.current = true;
      }
    }, 0);

    return () => {
      clearTimeout(timeout);
    };
  }, [lastUpdatedTasks, task, structuredData]);

  const logLabelEvent = useCallback(
    (event: LabelingEvent) => {
      logEvent(dispatch, { event, data: logCommonData });
    },
    [dispatch, logCommonData]
  );

  const handleCorrectionChange = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    let isCorrect: boolean | null;
    switch (event.currentTarget.value) {
      case "makeCorrection":
        logLabelEvent(LabelingEvent.MakeCorrection);
        isCorrect = false;
        break;
      case "markCorrect":
        logLabelEvent(LabelingEvent.MarkCorrect);
        isCorrect = true;
        break;
      case "removeCorrection":
        logLabelEvent(LabelingEvent.RemoveCorrection);
        isCorrect = null;
        break;
      default:
        isCorrect = null;
    }

    saveData({ correct_label: isCorrect });
    dispatch(updateQCCorrectLabels({ task, correct: isCorrect, messageUID }));
  };

  const onChangeEvent = useCallback(() => {
    if (isQC) {
      const taskLabels = getTaskLabels(
        task.id,
        existingLabels?.labels,
        messageUID
      );
      if (taskLabels) {
        saveData(getQCCorrectedLabels(taskLabels.label_data, getValues()));
      }
    } else {
      saveData(getValues());
    }
  }, [task.id, existingLabels, isQC, messageUID, getValues, saveData]);

  const handleResetDialogOpen = () => {
    setOpen(true);
  };

  const handleResetDialogueClose = () => {
    setOpen(false);
  };

  const handleReset = useCallback(() => {
    dispatch(
      updateTaskStatuses({
        taskID: task.id,
        newTaskStatus: CompletionStatus.new,
        messageUID: messageUID,
      })
    );
    logLabelEvent(LabelingEvent.ClearForm);
    if (isQC) {
      const taskLabels = getTaskLabels(
        task.id,
        existingLabels?.labels,
        messageUID
      );
      reset(taskLabels?.label_data);
      saveData({ correct_label: isCorrect });
    } else if (isLabeler) {
      const defaultValues = generateFormDefaultValues(formData);
      reset(defaultValues);
      saveData({});
    }
    handleResetDialogueClose();
  }, [
    dispatch,
    task.id,
    messageUID,
    logLabelEvent,
    isQC,
    isLabeler,
    existingLabels?.labels,
    reset,
    saveData,
    isCorrect,
    formData,
  ]);

  const qcCorrectedLabels = useMemo<string[]>(() => {
    if (!isQC && !isMonitor) return [];
    const labels = isMonitor
      ? allExistingLabels?.labels
      : existingLabels?.labels;
    const taskLabels = getTaskLabels(task.id, labels, messageUID);
    if (!taskLabels) return [];

    const qcCorrected = getQCCorrectedLabels(taskLabels.label_data, formData);
    return Object.keys(qcCorrected);
  }, [
    isQC,
    isMonitor,
    allExistingLabels?.labels,
    existingLabels?.labels,
    task.id,
    messageUID,
    formData,
  ]);

  const displayResetButton = () => {
    return (
      <>
        <Box sx={styles.buttonBox}>
          <Button
            variant="outlined"
            startIcon={<Delete />}
            onClick={handleResetDialogOpen}
          >
            {t("form.clearForm")}
          </Button>
        </Box>
        <Dialog
          open={open}
          onClose={handleResetDialogueClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {t("form.clearFormModalTitle")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("form.clearFormModalBody")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleResetDialogueClose}>Cancel</Button>
            <Button onClick={handleReset} autoFocus>
              Yes, reset
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const renderQuestionField = (
    q: Question,
    parentKey?: string,
    possibleResponses?: string[]
  ): React.JSX.Element | null => {
    const fieldName = parentKey ? `${parentKey}.${q.id}` : q.id;
    if (q.dependsOn) {
      const dependencyField = parentKey
        ? `${parentKey}.${q.dependsOn.questionId}.response`
        : `${q.dependsOn.questionId}.response`;
      const dependencyValue = watch(dependencyField);
      if (
        !dependencyValue ||
        !valueMatches(q.dependsOn.values, dependencyValue as string)
      ) {
        return null;
      }
    }
    if (q.type === "select") {
      const noFlagsMessage = (
        <Typography variant="subtitle2" color="info" gutterBottom>
          No flags on the conversation.
        </Typography>
      );

      const renderFlagChips = () => {
        if (!conversation) return null;

        if (conversation.flags.length === 0) {
          return noFlagsMessage;
        }

        return (
          <Stack direction="row" spacing={1}>
            {conversation.flags.map((flag) => (
              <SmallChip key={flag} flagData={getFlagOption(flag)} />
            ))}
          </Stack>
        );
      };

      const renderInstructions = () =>
        q.additionalInstructions ? (
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            {q.additionalInstructions}
          </Typography>
        ) : null;

      if (q.perFlag) {
        if (!conversation) return null;

        const knownFlags = conversation.flags
          .map((flag) => ({ flag, flagOption: getFlagOption(flag) }))
          .filter((entry): entry is { flag: string; flagOption: FlagOption } =>
            Boolean(entry.flagOption)
          );

        return (
          <section key={fieldName}>
            {renderInstructions()}

            {knownFlags.length === 0 ? (
              noFlagsMessage
            ) : (
              <Stack spacing={2}>
                {knownFlags.map(({ flagOption }) => {
                  const flagFieldName = `${fieldName}.${flagOption.id}`;

                  return (
                    <Box key={flagFieldName}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                        <SmallChip flagData={flagOption} />
                      </Stack>

                      <Typography gutterBottom>{q.name}</Typography>

                      <SelectQuestionComponent
                        name={flagFieldName}
                        question={q}
                        possibleResponses={possibleResponses}
                        displayDefinition={q.displayDefinition}
                        onChangeEvent={onChangeEvent}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}
          </section>
        );
      }

      return (
        <section key={fieldName}>
          <Typography gutterBottom>{q.name}</Typography>

          {renderInstructions()}

          {q.displayFlags && renderFlagChips()}

          <SelectQuestionComponent
            name={fieldName}
            question={q}
            possibleResponses={possibleResponses}
            displayDefinition={q.displayDefinition}
            onChangeEvent={onChangeEvent}
          />
        </section>
      );
    }
    if (q.type === "text") {
      return (
        <section key={fieldName}>
          <Typography gutterBottom>{q.name}</Typography>
          <TextQuestionComponent
            name={fieldName}
            question={q}
            onChangeEvent={onChangeEvent}
          />
        </section>
      );
    } else {
      return (
        <section key={fieldName}>
          <Typography gutterBottom>{q.name}</Typography>
          <MultiSelectQuestionComponent
            name={fieldName}
            question={q}
            possibleResponses={possibleResponses}
            displayFlags={q.displayFlags}
            onChangeEvent={onChangeEvent}
          />
        </section>
      );
    }
  };

  const handleLastUpdatedTasks = (metric: string) => {
    dispatch(
      updateLastUpdatedTasks({
        taskId: task.id,
        metric: metric,
      })
    );
  };

  const getLabeledContent = (fieldKey: string) => {
    const labels = isMonitor
      ? allExistingLabels?.labels
      : existingLabels?.labels;
    const labeledContent = getTaskLabels(
      task.id,
      labels,
      messageUID
    )?.labeled_content;

    const field = labeledContent?.[fieldKey];

    if (field && typeof field === "object" && "value" in field) {
      return field.value;
    }

    return field;
  };

  if (isLoading) return <InfoAlert message="Loading form..." />;
  if (error) return <ErrorAlert error={error} />;

  /**
   * Render the labeling form.
   * If the task is structured data, render the fields and questions accordingly.
   * If the task is unstructured data, render the questions directly.
   */
  return (
    <Box
      sx={{
        display:
          isMonitor &&
          (qcCorrectedLabels.length === 0 ||
            (qcCorrectedLabels.length === 1 &&
              qcCorrectedLabels[0] === "correct_label")) &&
          formProvider === LabelingRole.conversation_label_qc
            ? "none"
            : "flex",
        flexDirection: "row",
        alignItems: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: 1,
        }}
      >
        {!isLabeler && formProvider === LabelingRole.conversation_labeler && (
          <Typography variant="h5">{labeler?.name}'s labels</Typography>
        )}
        {!isLabeler && formProvider === LabelingRole.conversation_label_qc && (
          <Typography variant="h5">
            {isMonitor ? "QC" : "Your"} label corrections
          </Typography>
        )}
        <FormProvider {...methods}>
          <fieldset
            style={formSubmitted ? { pointerEvents: "none", opacity: 0.6 } : {}}
          >
            {!formSubmitted && displayResetButton()}
            {isStructuredDataTask(task.content) ? (
              Object.entries(structuredData).map(
                ([fieldKey, fieldData], index) => {
                  const labeledContentValue =
                    isQC || isMonitor
                      ? (getLabeledContent(fieldKey) ?? "")
                      : fieldData.value;
                  const showLabeledContentDiffIcon =
                    (isQC || isMonitor) &&
                    labeledContentValue !== undefined &&
                    formatValueAsString(labeledContentValue).toLowerCase() !==
                      formatValueAsString(fieldData.value).toLowerCase();
                  return (
                    <Box
                      id={`structured-data-${fieldKey}`}
                      key={fieldKey}
                      sx={styles.fieldContainer}
                      onClick={() => {
                        handleLastUpdatedTasks(`structured-data-${fieldKey}`);
                      }}
                    >
                      <Typography variant="subtitle1">
                        {`${index + 1}. ${fieldData.description.replace(
                          /\.$/,
                          ""
                        )}: `}
                        <span style={{ fontWeight: "bold" }}>
                          {formatValueAsString(labeledContentValue)}
                          {showLabeledContentDiffIcon && (
                            <Tooltip title={t("form.labeledContentDiffNote")}>
                              <ErrorOutlineIcon
                                color="warning"
                                sx={styles.labeledContentDiffIcon}
                              />
                            </Tooltip>
                          )}
                        </span>
                      </Typography>
                      <List>
                        {questions.map((q: Question) => {
                          const isQCCorrected =
                            qcCorrectedLabels.includes(fieldKey);
                          return (
                            <ListItem
                              key={`${fieldKey}.${q.id}`}
                              sx={{
                                width: "85%",
                                display: "block",
                                ...(isQCCorrected && {
                                  backgroundColor: grey[100],
                                }),
                              }}
                            >
                              {renderQuestionField(
                                q,
                                fieldKey,
                                fieldData.possible_responses
                              )}
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  );
                }
              )
            ) : (
              <List>
                {questions.map((q) => {
                  const isQCCorrected = qcCorrectedLabels.includes(q.id);
                  return (
                    <ListItem
                      key={q.id}
                      sx={{
                        width: "85%",
                        display: "block",
                        ...(isQCCorrected && {
                          backgroundColor: grey[100],
                        }),
                      }}
                    >
                      {renderQuestionField(q)}
                    </ListItem>
                  );
                })}
              </List>
            )}
            {!formSubmitted && (
              <Box sx={styles.buttonBox}>
                {!isCorrect && !isLabeler && (
                  <Button
                    variant="contained"
                    color="error"
                    value="removeCorrection"
                    onClick={(event) => {
                      handleCorrectionChange(event);
                      handleLastUpdatedTasks("");
                    }}
                  >
                    {t("form.removeCorrection")}
                  </Button>
                )}
              </Box>
            )}
          </fieldset>
        </FormProvider>

        {usesStagingApi() && debugEnabled && (
          <FormProvider {...methods}>
            <DebugPanel />
          </FormProvider>
        )}

        <SnackbarAlert
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => {
            setSnackbar((prev) => ({ ...prev, open: false }));
          }}
        />
      </Box>
      {!isLabeler &&
        formProvider === LabelingRole.conversation_labeler &&
        (isCorrect === null || isCorrect === true) &&
        !hasSubmittedLabels(qc?.qc_status) && (
          <div style={{ position: "sticky", top: "20px", marginLeft: "10px" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {isCorrect === null && (
                <Button
                  variant="contained"
                  color={"success"}
                  value="markCorrect"
                  disabled={isMonitor}
                  onClick={(event) => {
                    handleCorrectionChange(event);
                  }}
                >
                  {t("form.markCorrect")}
                </Button>
              )}
              {formSubmitted && (
                <Button
                  variant="contained"
                  value="makeCorrection"
                  disabled={isMonitor}
                  onClick={(event) => {
                    handleCorrectionChange(event);
                  }}
                >
                  {t("form.makeCorrection")}
                </Button>
              )}
            </Box>
          </div>
        )}
    </Box>
  );
};

const styles = createStyleSheet({
  fieldContainer: {
    ...globalStyles.flexColumn,
    marginBottom: 2,
  },
  buttonBox: {
    display: "flex",
    justifyContent: "flex-end",
    margin: 1,
  },
  labeledContentDiffIcon: {
    verticalAlign: "middle",
    ml: 0.5,
    pointerEvents: "all",
  },
});

export default LabelingForm;
